import { Icon } from "@iconify-icon/react";
import { useTranslation } from "react-i18next";
import { motion } from "motion/react";

import { Button } from "@/components/ui/button.tsx";
import { Card } from "@/components/ui/card.tsx";
import { useIsMobile } from "@/hooks/use-mobile.tsx";

// Animated Logo Component with Shine Effect
const AnimatedLogo: React.FC<{ className?: string }> = ({ className = "" }) => {
  return (
    <div className={`relative inline-block h-full ${className}`}>
      {/* Main Logo */}
      <svg
        className="h-8 w-auto lg:h-12"
        fill="none"
        viewBox="0 0 309 82"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g>
          {/* Logo mark (first 3 paths) */}
          <motion.path
            animate={{ opacity: 1 }}
            d="M40.2817,51.9956c2.0586,3.0431,4.465,7.2455,6.3575,12.56639c1.6226,4.5624,2.4213,8.6929,2.821,11.9995
            c0.0543,0.4915,0.2859,2.0163,1.5522,3.3474c1.1644,1.22421,2.5254,1.57121,3.0261,1.67371c7.3248,0,14.6495,0,21.9743,0
            c0.2642,0,1.8446-0.0259,3.0209-1.3093c0.9357-1.0209,1.0361-2.2054,1.0528-2.551c-0.5132-3.9285-1.6866-9.0098-4.4138-14.3751
            c-7.2044-14.174-20.1013-20.1948-24.6603-22.0689C47.4355,44.8507,43.8586,48.4232,40.2817,51.9956z"
            fill="currentColor"
            initial={{ opacity: 0 }}
            transition={{ duration: 0.6, delay: 0 * 0.1 }}
          />
          <motion.path
            animate={{ opacity: 1 }}
            d="M3.7668,0.6599c0.2579-0.1191,0.54-0.1715,0.8242-0.1707c7.7486,0.0176,15.49718,0.0353,23.24572,0.053
            c0.66225,0.0532,1.60919,0.2321,2.45666,0.8639c2.24061,1.6702,1.12578,4.5233,2.26757,10.2312
            c0.39369,1.9678,1.59228,6.9586,2.75357,10.3123c0.15646,0.4519,0.72492,1.7839,0.59392,3.3743
            c-0.10613,1.2889-0.59392,2.2677-1.02581,2.8617c-1.07296,1.4072-2.60288,3.1324-4.69727,4.8051
            c-1.76815,1.4122-3.47133,2.4135-4.89084,3.1204c-0.01541,0.0076-0.02844,0.0137-0.04436,0.0202
            c-0.61421,0.2551-1.71234,0.6145-3.10946,0.5577c-1.10469-0.0448-1.99513-0.3013-2.53753-0.5668
            C16.0423,34.3793,10.6785,29.8088,6.3484,22.1385c0,0-3.664-6.0466-4.9132-15.3873C1.2263,5.1893,1.1111,3.0956,2.407,1.6491
            C2.8428,1.1627,3.3427,0.8559,3.7668,0.6599z"
            fill="currentColor"
            initial={{ opacity: 0 }}
            transition={{ duration: 0.6, delay: 1 * 0.1 }}
          />
          <motion.path
            animate={{ opacity: 1 }}
            d="M52.8777,0.5149c0.1672-0.0559,0.3391-0.0807,0.5154-0.0807h23.5755c0.29031,0,1.9085,0.4302,2.65231,1.6063
            c0.6374,1.0078,0.7782,1.9832,0.7491,2.3079c0,1.3094-0.0612,2.654-0.189,3.9569c-0.3989,4.0678-2.8953,11.7484-9.0434,19.2863
            c-4.7794,5.8598-10.2043,9.1514-13.4438,11.0141c-3.8227,2.1982-11.3446,6.6728-17.4121,15.4685
            c-6.63436,9.6173-7.55971,19.1727-8.46308,23.5266c-0.11747,0.5661-0.53285,1.38-1.2148,2.2271
            c-0.36763,0.4566-0.75287,0.81989-1.11136,1.1058c-0.53176,0.4238-1.19043,0.6489-1.87049,0.6489H5.5676
            c-0.4103-0.0097-1.5847-0.0912-2.7016-0.9448c-0.9563-0.731-1.1896-1.662-1.2688-2.0787
            c-0.7241-3.8078,1.0605-10.9269,6.0065-18.4109c5.3827-8.1447,11.71698-12.8397,15.14445-14.861
            c4.31123-2.6355,12.02775-7.0957,19.35575-16.9667c7.2612-9.781,7.5368-19.6813,7.7342-23.9721
            c0.0364-0.39,0.1959-1.5537,1.1338-2.5914C51.6409,1.0167,52.4054,0.673,52.8777,0.5149z"
            fill="currentColor"
            initial={{ opacity: 0 }}
            transition={{ duration: 0.6, delay: 2 * 0.1 }}
          />

          {/* Text paths */}
          <motion.path
            animate={{ opacity: 1 }}
            d="M93.9799,3.4056h10.11301c1.5093,0,2.73289,1.2235,2.73289,2.7328v72.0434
            c0,1.5093-1.22359,2.7329-2.73289,2.7329H93.9799c-1.5094,0-2.73289-1.2236-2.73289-2.7329V6.1384
            C91.247,4.6291,92.4705,3.4056,93.9799,3.4056z"
            fill="currentColor"
            initial={{ opacity: 0 }}
            transition={{ duration: 0.6, delay: 3 * 0.1 }}
          />
          <motion.path
            animate={{ opacity: 1 }}
            d="M120.0502,6.1745v72.1222c0,1.4459,1.1721,2.618,2.6179,2.618h10.58199
            c1.44591,0,2.61801-1.1721,2.61801-2.618V55.7413c0-2.5621,3.30009-3.599,4.7654-1.4972l8.71809,12.5044
            c4.3389,6.2233,13.5594,6.1928,17.85701-0.059l8.64909-12.5821c1.45641-2.1187,4.7753-1.088,4.7753,1.483v22.7063
            c0,1.4459,1.1721,2.618,2.6179,2.618h10.4827c1.4458,0,2.6179-1.1721,2.6179-2.618V6.1745c0-1.4458-1.1721-2.6179-2.6179-2.6179
            h-8.5153c-0.97969,0-1.8774,0.547-2.3266,1.4176l-18.9903,36.8082c-2.37241,4.5984-8.93761,4.6273-11.3503,0.0499L133.112,4.9538
            c-0.4529-0.8594-1.3445-1.3972-2.3159-1.3972h-8.128C121.2223,3.5566,120.0502,4.7287,120.0502,6.1745z"
            fill="currentColor"
            initial={{ opacity: 0 }}
            transition={{ duration: 0.6, delay: 4 * 0.1 }}
          />
          <motion.path
            animate={{ opacity: 1 }}
            d="M256.41309,68.3809v9.585c0,1.6286-1.32019,2.94881-2.94868,2.94881H212.6256
            c-1.62849,0-2.9487-1.32021-2.9487-2.94881V6.3543c0-1.6285,1.32021-2.9487,2.9487-2.9487h9.6813
            c1.62849,0,2.9487,1.3202,2.9487,2.9487v56.1291c0,1.6285,1.32019,2.9487,2.94881,2.9487h25.25999
            C255.0929,65.4321,256.41309,66.7523,256.41309,68.3809z"
            fill="currentColor"
            initial={{ opacity: 0 }}
            transition={{ duration: 0.6, delay: 5 * 0.1 }}
          />
          <motion.path
            animate={{ opacity: 1 }}
            d="M268.74759,47.1545l-21.9796-40.5243c-0.78909-1.4549,0.26421-3.2246,1.9194-3.2246h12.60912
            c0.81149,0,1.556,0.45,1.93329,1.1685l11.5903,22.0752c0.81799,1.5581,3.04871,1.558,3.8667-0.0001l11.5885-22.075
            c0.3772-0.7186,1.1218-1.1686,1.93338-1.1686h12.655c1.65601,0,2.70932,1.7712,1.91852,3.2263l-22.02289,40.5219
            c-0.17392,0.32-0.26501,0.6785-0.26501,1.0427v30.5346c0,1.20589-0.9776,2.1836-2.18359,2.1836h-11.11542
            c-1.20587,0-2.18359-0.97771-2.18359-2.1836V48.1955C269.01169,47.8319,268.9209,47.4741,268.74759,47.1545z"
            fill="currentColor"
            initial={{ opacity: 0 }}
            transition={{ duration: 0.6, delay: 6 * 0.1 }}
          />
        </g>
      </svg>
    </div>
  );
};

interface WelcomeStepProps {
  onNext: () => void;
}

const WelcomeStep: React.FC<WelcomeStepProps> = ({ onNext }) => {
  const { t } = useTranslation(["onboarding", "common"]);
  const isMobile = useIsMobile();

  return (
    <div className="relative w-full space-y-6 px-3 sm:space-y-8 sm:px-0">
      {/* Background circles */}
      <motion.div
        layout
        animate={{ opacity: 0.2 }}
        className="duration-0.75 absolute -bottom-50 -left-50 size-120 rounded-full bg-gradient-to-br from-blue-500/20 to-purple-600/20 opacity-30 transition-colors"
        initial={{ opacity: 0 }}
        layoutId="circle1"
        transition={{
          default: { duration: 0.75 },
          layout: { duration: 0.75 },
        }}
      />
      <motion.div
        layout
        animate={{ opacity: 0.2 }}
        className="duration-0.75 absolute -top-50 -right-50 size-120 rounded-full bg-gradient-to-br from-green-500/20 to-cyan-500/20 opacity-30 transition-colors"
        initial={{ opacity: 0.2 }}
        layoutId="circle2"
        transition={{
          layout: { duration: 0.75 },
        }}
      />

      {/* Header Section */}
      <motion.div
        animate={{ opacity: 1, y: 0 }}
        className="space-y-4 text-center sm:space-y-6"
        initial={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.4 }}
      >
        <div className="mt-16 min-[450px]:mt-0">
          <h1 className="text-accent-foreground text-4xl font-bold lg:text-5xl xl:text-6xl">
            {t("common:welcomeTo")} <AnimatedLogo className="text-accent-foreground" />
          </h1>
          <p className="text-muted-foreground mt-2 text-lg sm:mt-4 sm:text-xl md:text-2xl">
            {t("onboarding:welcome.subtitle", "Your all-in-one teaching platform")}
          </p>
        </div>
      </motion.div>

      {/* Features Grid */}
      <motion.div
        animate={{ opacity: 1, y: 0 }}
        initial={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.4, delay: 0.2 }}
      >
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3">
          {[
            {
              title: t("onboarding:welcome.features.marketplace", "Marketplace"),
              description: isMobile
                ? t(
                    "onboarding:welcome.features.marketplaceDescMobile",
                    "Reach students worldwide with your teaching profile.",
                  )
                : t(
                    "onboarding:welcome.features.marketplaceDesc",
                    "Connect with students worldwide. Create your teaching profile and showcase your expertise to attract learners from around the globe.",
                  ),
              icon: "mdi:store",
              bgGradient: "from-green-500/10 to-emerald-600/10",
              iconBg: "bg-green-500/20",
            },
            {
              title: t("onboarding:welcome.features.booking", "Booking System"),
              description: isMobile
                ? t(
                    "onboarding:welcome.features.bookingDescMobile",
                    "Set availability and let students book across time zones.",
                  )
                : t(
                    "onboarding:welcome.features.bookingDesc",
                    "Flexible scheduling that works for you. Set your availability, manage time zones, and let students book lessons at their convenience.",
                  ),
              icon: "mdi:calendar-clock",
              bgGradient: "from-blue-500/10 to-indigo-600/10",
              iconBg: "bg-blue-500/20",
            },
            {
              title: t("onboarding:welcome.features.payments", "Payments Management"),
              description: isMobile
                ? t(
                    "onboarding:welcome.features.paymentsDescMobile",
                    "Get paid instantly with secure, transparent pricing.",
                  )
                : t(
                    "onboarding:welcome.features.paymentsDesc",
                    "Secure and instant payment processing. Get paid immediately when students book lessons, with transparent pricing and no hidden fees.",
                  ),
              icon: "mdi:credit-card-multiple",
              bgGradient: "from-purple-500/10 to-violet-600/10",
              iconBg: "bg-purple-500/20",
            },
            {
              title: t("onboarding:welcome.features.communication", "In-App Communication"),
              description: isMobile
                ? t(
                    "onboarding:welcome.features.communicationDescMobile",
                    "Message students, share materials, and track progress.",
                  )
                : t(
                    "onboarding:welcome.features.communicationDesc",
                    "Built-in messaging system for seamless communication. Share materials, discuss progress, and maintain professional relationships with your students.",
                  ),
              icon: "mdi:message-text",
              bgGradient: "from-orange-500/10 to-red-600/10",
              iconBg: "bg-orange-500/20",
            },
            {
              title: t("onboarding:welcome.features.video", "Best Class Video"),
              description: isMobile
                ? t(
                    "onboarding:welcome.features.videoDescMobile",
                    "HD classes with screen share, whiteboard, and interactivity.",
                  )
                : t(
                    "onboarding:welcome.features.videoDesc",
                    "High-quality video classrooms with screen sharing, whiteboard tools, and interactive features. Deliver engaging lessons from anywhere in the world.",
                  ),
              icon: "mdi:video",
              bgGradient: "from-pink-500/10 to-rose-600/10",
              iconBg: "bg-pink-500/20",
            },
            {
              title: t("onboarding:welcome.features.more", "And Much More"),
              description: isMobile
                ? t(
                    "onboarding:welcome.features.moreDescMobile",
                    "Tools for tracking, certificates, and analytics - all in one.",
                  )
                : t(
                    "onboarding:welcome.features.moreDesc",
                    "Complete teaching toolkit with progress tracking, lesson materials, certificates, and analytics. Everything you need to grow your teaching business.",
                  ),
              icon: "mdi:plus-circle",
              bgGradient: "from-gray-500/10 to-slate-600/10",
              iconBg: "bg-gray-500/20",
            },
          ].map((feature, index) => (
            <Card
              key={index}
              className="bg-foreground/50 text-accent-foreground group relative overflow-hidden rounded-xl border-none p-3 shadow-lg transition-all duration-300 hover:scale-[1.02] hover:shadow-xl sm:rounded-2xl sm:p-6"
            >
              {/* Background Gradient */}
              <div
                className={`absolute inset-0 bg-gradient-to-br ${feature.bgGradient} opacity-0 transition-opacity duration-300 group-hover:opacity-100`}
              />

              <div className="relative flex flex-col">
                <div className="mb-3 flex items-center gap-3">
                  <div
                    className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg sm:h-10 sm:w-10 sm:rounded-xl ${feature.iconBg}`}
                  >
                    <Icon
                      className="text-accent-foreground"
                      height="1rem"
                      icon={feature.icon}
                      width="1rem"
                    />
                  </div>
                  <h3 className="text-accent-foreground text-sm font-semibold sm:text-base">
                    {feature.title}
                  </h3>
                </div>

                <div className="text-left">
                  <p className="text-muted-foreground text-xs leading-relaxed sm:text-sm">
                    {feature.description}
                  </p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </motion.div>

      {/* Navigation */}
      <motion.div
        animate={{ opacity: 1, y: 0 }}
        className="relative z-50 flex justify-center"
        initial={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.4, delay: 0.3 }}
      >
        <Button
          className="w-full px-6 sm:px-8 lg:w-2/5"
          size="lg"
          variant={"foreground"}
          onClick={onNext}
        >
          {t("onboarding:navigation.getStarted", "Get Started")}
          <Icon className="ml-2" height="1rem" icon="mdi:arrow-right" width="1rem" />
        </Button>
      </motion.div>
    </div>
  );
};

export default WelcomeStep;
